// LeadsKanbanView — pipeline board for /leads.
// One column per stage. HTML5 native drag-and-drop moves a card between
// columns; the parent handles the PUT /api/leads/:id to persist the stage
// change. Card click opens the lead detail page.
//
// Design ref: bemarin-style — colored left border per stage, name + secondary
// line, deal value bottom-left, assignee avatar bottom-right, relative time
// top-right (redder as it ages).

import { memo, useMemo, useState } from "react";

const HEX_BY_TW = {
  sky:      "#0EA5E9",
  blue:     "#3B82F6",
  indigo:   "#6366F1",
  violet:   "#8B5CF6",
  amber:    "#F59E0B",
  orange:   "#F97316",
  emerald:  "#10B981",
  green:    "#22C55E",
  rose:     "#F43F5E",
  red:      "#EF4444",
  teal:     "#14B8A6",
  cyan:     "#06B6D4",
  slate:    "#64748B",
  gray:     "#6B7280",
};

// Extract "sky" / "blue" / etc. from a bg-sky-100 string
function paletteFromBadgeClass(cls) {
  if (!cls) return "slate";
  const m = String(cls).match(/bg-([a-z]+)-\d+/);
  return (m && HEX_BY_TW[m[1]]) ? m[1] : "slate";
}

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diffMs = Date.now() - t;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.floor(diffDay / 30);
  return `${diffMo}mo ago`;
}

// Age-based color: fresh = neutral, older = warmer
function ageColor(iso) {
  if (!iso) return "text-slate-400";
  const diffDay = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (diffDay < 1) return "text-slate-400";
  if (diffDay < 3) return "text-slate-500";
  if (diffDay < 7) return "text-amber-600";
  return "text-rose-600";
}

function formatINR(n) {
  const v = Number(n);
  if (!v) return "";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${Math.round(v / 1e3)}K`;
  return `₹${v.toLocaleString("en-IN")}`;
}

function initials(name) {
  return String(name || "?")
    .split(" ")
    .map(s => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
}

// Deterministic hue from a string (for avatars)
function avatarBg(seed) {
  const palette = ["#818CF8", "#F59E0B", "#0EA5E9", "#10B981", "#F43F5E", "#8B5CF6", "#F97316", "#14B8A6"];
  let h = 0;
  for (let i = 0; i < String(seed).length; i++) h = (h * 31 + String(seed).charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

// ─── Card ───────────────────────────────────────────────────────────────────

function LeadCardImpl({ lead, accentHex, onOpen, onDragStart, onDragEnd, dragging }) {
  const name = lead.name || lead.company_name || lead.email || lead.phone || "Unnamed";
  const secondary = lead.company_name && lead.company_name !== name
    ? lead.company_name
    : (lead.source || "");
  const time = relativeTime(lead.created_at || lead.updated_at);
  const assignedName = lead.assigned_to_name || "";

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen && onOpen(lead)}
      className={`group cursor-pointer bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-3 hover:shadow-md transition ${dragging ? "opacity-40" : ""}`}
      style={{ borderLeft: `4px solid ${accentHex}` }}
      data-testid={`kanban-card-${lead.lead_id || lead._id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-slate-900 dark:text-white truncate leading-tight">{name}</div>
          {secondary && <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{secondary}</div>}
        </div>
        {time && <div className={`text-[10.5px] font-semibold whitespace-nowrap shrink-0 ${ageColor(lead.created_at || lead.updated_at)}`}>{time}</div>}
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="text-[12px] font-bold text-slate-800 dark:text-slate-200">{formatINR(lead.deal_value)}</div>
        {assignedName ? (
          <div
            title={assignedName}
            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ backgroundColor: avatarBg(assignedName) }}
          >
            {initials(assignedName)}
          </div>
        ) : (
          <div className="text-[10px] text-slate-400">Unassigned</div>
        )}
      </div>
    </div>
  );
}
const LeadCard = memo(LeadCardImpl);

// ─── Column ─────────────────────────────────────────────────────────────────

function Column({ stage, hex, leads, dragOver, onCardOpen, onDragStart, onDragEnd, onDragOverColumn, onDragLeaveColumn, onDropIntoColumn, dragging }) {
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); onDragOverColumn(stage); }}
      onDragLeave={() => onDragLeaveColumn(stage)}
      onDrop={(e) => { e.preventDefault(); onDropIntoColumn(stage); }}
      className={`flex-1 min-w-[280px] max-w-[320px] flex flex-col rounded-2xl transition ${dragOver ? "ring-2 ring-indigo-400 ring-offset-2" : ""}`}
      data-testid={`kanban-column-${stage.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 mb-3 rounded-xl border"
        style={{
          background: `${hex}18`,            // ~9% alpha fill
          borderColor: `${hex}55`,           // ~33% alpha border
        }}
      >
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: hex }} />
        <h3 className="text-[13px] font-bold" style={{ color: hex }}>
          {stage}
          <span className="ml-1.5 font-semibold text-[12px] px-1.5 py-0.5 rounded-full"
                style={{ background: `${hex}33`, color: hex }}>
            {leads.length}
          </span>
        </h3>
      </div>
      <div className={`flex flex-col gap-2 min-h-[100px] p-1 rounded-xl ${dragOver ? "bg-indigo-50/50 dark:bg-indigo-900/10" : ""}`}>
        {leads.length === 0 ? (
          <div className="text-[11px] text-slate-400 italic text-center py-6">Drag a lead here</div>
        ) : (
          leads.map(lead => (
            <LeadCard
              key={lead.lead_id || lead._id}
              lead={lead}
              accentHex={hex}
              onOpen={onCardOpen}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              dragging={dragging?.lead_id === (lead.lead_id || lead._id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

function LeadsKanbanViewImpl({
  leads = [],
  stages = [],
  stageColor = {},        // { "New Lead": "bg-sky-100 text-sky-700 border …" }
  onCardClick,            // (lead) => open detail
  onStageChange,          // async (lead, newStage) => update lead's stage
}) {
  const [dragging, setDragging] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Group leads by stage; unknown stages fall into their own bucket
  const byStage = useMemo(() => {
    const m = new Map();
    for (const s of stages) m.set(s, []);
    for (const l of leads) {
      const s = l.stage || "New Lead";
      if (!m.has(s)) m.set(s, []);
      m.get(s).push(l);
    }
    return m;
  }, [leads, stages]);

  // Resolve accent hex per stage from Tailwind badge classes
  const stageHex = useMemo(() => {
    const out = {};
    for (const s of byStage.keys()) {
      const pal = paletteFromBadgeClass(stageColor[s]);
      out[s] = HEX_BY_TW[pal] || HEX_BY_TW.slate;
    }
    return out;
  }, [byStage, stageColor]);

  const handleDragStart = (e, lead) => {
    setDragging(lead);
    try { e.dataTransfer.effectAllowed = "move"; } catch (_) {}
    try { e.dataTransfer.setData("text/plain", lead.lead_id || lead._id); } catch (_) {}
  };
  const handleDragEnd = () => { setDragging(null); setDragOverStage(null); };
  const handleDropIntoColumn = (stage) => {
    if (dragging && dragging.stage !== stage) {
      onStageChange && onStageChange(dragging, stage);
    }
    setDragging(null);
    setDragOverStage(null);
  };

  return (
    <div className="overflow-x-auto pb-4" data-testid="leads-kanban-view">
      <div className="flex gap-4 min-w-max px-1">
        {[...byStage.keys()].map(stage => (
          <Column
            key={stage}
            stage={stage}
            hex={stageHex[stage]}
            leads={byStage.get(stage) || []}
            dragOver={dragOverStage === stage}
            dragging={dragging}
            onCardOpen={onCardClick}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOverColumn={setDragOverStage}
            onDragLeaveColumn={(s) => setDragOverStage(prev => prev === s ? null : prev)}
            onDropIntoColumn={handleDropIntoColumn}
          />
        ))}
      </div>
    </div>
  );
}

export default memo(LeadsKanbanViewImpl);
